import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  StorePriceDisplay,
  StorePriceEachDisplay,
} from "./StorePriceDisplay";
import {
  formatSavedAddressSummary,
} from "../utils/checkoutAddressFormat";
import {
  localizedStoreProductName,
  getStoreCartLineUnitPrice,
  formatStoreDeliveryEstimate,
} from "../../../utils/storeUtils";

const CartCheckout = forwardRef(function CartCheckout(
  {
    id,
    cart,
    cartTotal,
    checkoutForm,
    submitting,
    deliveryReady,
    savedAddresses = [],
    selectedSavedAddressId,
    onSelectSavedAddress,
    onDeleteSavedAddress,
    onOpenAddAddressModal,
    canAddMoreAddresses,
    onUpdateQuantity,
    onFormChange,
    onSubmit,
    firstName = "",
    allowGuestCheckout = false,
    onOpenGuestAddressModal,
  },
  ref
) {
  const { t, i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const shippingFee = 0;
  const grandTotal = Number(cartTotal || 0) + shippingFee;

  const hasSavedSelection =
    !!selectedSavedAddressId &&
    savedAddresses.some(
      (a) => String(a._id) === String(selectedSavedAddressId)
    );

  return (
    <div
      ref={ref}
      id={id}
      className="store-checkout store-checkout--page"
      aria-labelledby="store-checkout-heading"
      dir={isRtl ? "rtl" : "ltr"}
      lang={isRtl ? "ar" : "en"}
    >
      <form className="store-checkout-layout" onSubmit={onSubmit} noValidate>
        <div className="store-checkout-main-card">
          <div className="store-checkout-heading-row">
            <h3 id="store-checkout-heading">
              {t("store.checkout.pageTitle", {
                defaultValue: "Cart & Checkout",
              })}
            </h3>
            <span className="store-checkout-items-badge">
              {cart.length === 1
                ? t("store.checkout.itemBadgeOne", {
                    count: cart.length,
                    defaultValue: "{{count}} item",
                  })
                : t("store.checkout.itemBadgeOther", {
                    count: cart.length,
                    defaultValue: "{{count}} items",
                  })}
            </span>
          </div>
          <div className="store-cart-list">
            {cart.length === 0 ? (
              <div className="store-cart-empty">
                <p className="store-cart-empty-msg">
                  {firstName
                    ? t("store.checkout.emptyWithName", {
                        name: firstName,
                        defaultValue:
                          "Hi {{name}}! Your cart is empty. Browse products and add your favorites.",
                      })
                    : t("store.checkout.emptyGeneric", {
                        defaultValue:
                          "Your cart is empty. Browse products and add your favorites.",
                      })}
                </p>
                <Link to="/store" className="store-cart-empty-link">
                  {t("store.checkout.browseProducts", {
                    defaultValue: "Browse products",
                  })}
                </Link>
              </div>
            ) : (
              cart.map((item, idx) => {
                const lineName = localizedStoreProductName(item, i18n.language);
                const unit = getStoreCartLineUnitPrice(item);
                const lineDelivery = formatStoreDeliveryEstimate(item, t);
                const hasCurtainOptions =
                  item.selectedWidth != null ||
                  item.selectedHeight != null ||
                  (item.widthAdjustment && item.widthAdjustment !== "none") ||
                  (item.heightAdjustment && item.heightAdjustment !== "none") ||
                  item.chainType;
                const curtainOptionsLabel = hasCurtainOptions
                  ? [
                      item.selectedWidth != null ? `W: ${item.selectedWidth}cm` : "",
                      item.selectedHeight != null ? `H: ${item.selectedHeight}cm` : "",
                      item.widthAdjustment && item.widthAdjustment !== "none"
                        ? `W ${item.widthAdjustment}cm`
                        : "",
                      item.heightAdjustment && item.heightAdjustment !== "none"
                        ? `H ${item.heightAdjustment}cm`
                        : "",
                      item.chainType ? `Chain: ${item.chainType}` : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : "";
                return (
                <div
                  className="store-cart-item"
                  key={
                    item.selectedColor
                      ? `${item._id}-${item.selectedColor}-${idx}`
                      : item._id
                  }
                >
                  <div className="store-cart-item-main">
                    <div className="store-cart-item-thumb-wrap">
                      <span className="store-cart-item-thumb-fallback">
                        <i className="fa-regular fa-image" />
                      </span>
                      {item?.image?.url ? (
                        <img
                          src={item.image.url}
                          alt={
                            lineName ||
                            t("store.checkout.productAlt", {
                              defaultValue: "Product",
                            })
                          }
                          className="store-cart-item-thumb"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : null}
                    </div>
                    <div className="store-cart-item-meta">
                      <span className="store-cart-item-name">{lineName}</span>
                      {item.sku ? (
                        <span className="store-cart-item-sku" dir="ltr">
                          {item.sku}
                        </span>
                      ) : null}
                      {item.selectedColor && (
                        <span className="store-cart-item-color">
                          {t("store.checkout.colorLabel", {
                            color: item.selectedColor,
                            defaultValue: "Color: {{color}}",
                          })}
                        </span>
                      )}
                      {curtainOptionsLabel ? (
                        <span className="store-cart-item-color">{curtainOptionsLabel}</span>
                      ) : null}
                      <span className="store-cart-item-unit-price">
                        <StorePriceEachDisplay amount={unit} />
                      </span>
                      {lineDelivery ? (
                        <span className="store-cart-item-delivery">{lineDelivery}</span>
                      ) : null}
                    </div>
                  </div>

                  <div className="store-cart-item-qty">
                    <button
                      type="button"
                      className="store-cart-qty-btn"
                      onClick={() =>
                        onUpdateQuantity(
                          item._id,
                          Number(item.quantity || 1) - 1,
                          item.selectedColor,
                          {
                            selectedWidth: item.selectedWidth ?? null,
                            selectedHeight: item.selectedHeight ?? null,
                            widthAdjustment: item.widthAdjustment ?? "none",
                            heightAdjustment: item.heightAdjustment ?? "none",
                            chainType: item.chainType ?? "",
                          }
                        )
                      }
                      aria-label={t("store.checkout.decreaseQtyAria", {
                        name: lineName || "",
                        defaultValue: "Decrease quantity for {{name}}",
                      })}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) =>
                        onUpdateQuantity(
                          item._id,
                          e.target.value,
                          item.selectedColor,
                          {
                            selectedWidth: item.selectedWidth ?? null,
                            selectedHeight: item.selectedHeight ?? null,
                            widthAdjustment: item.widthAdjustment ?? "none",
                            heightAdjustment: item.heightAdjustment ?? "none",
                            chainType: item.chainType ?? "",
                          }
                        )
                      }
                      aria-label={t("store.checkout.qtyAria", {
                        name: lineName || "",
                        defaultValue: "Quantity for {{name}}",
                      })}
                    />
                    <button
                      type="button"
                      className="store-cart-qty-btn"
                      onClick={() =>
                        onUpdateQuantity(
                          item._id,
                          Number(item.quantity || 1) + 1,
                          item.selectedColor,
                          {
                            selectedWidth: item.selectedWidth ?? null,
                            selectedHeight: item.selectedHeight ?? null,
                            widthAdjustment: item.widthAdjustment ?? "none",
                            heightAdjustment: item.heightAdjustment ?? "none",
                            chainType: item.chainType ?? "",
                          }
                        )
                      }
                      aria-label={t("store.checkout.increaseQtyAria", {
                        name: lineName || "",
                        defaultValue: "Increase quantity for {{name}}",
                      })}
                    >
                      +
                    </button>
                  </div>

                  <div className="store-cart-item-right">
                    <span className="store-cart-item-line-total">
                      <StorePriceDisplay
                        amount={unit * Number(item.quantity || 0)}
                      />
                    </span>
                    <button
                      type="button"
                      className="store-cart-item-remove"
                      onClick={() =>
                        onUpdateQuantity(item._id, 0, item.selectedColor, {
                          selectedWidth: item.selectedWidth ?? null,
                          selectedHeight: item.selectedHeight ?? null,
                          widthAdjustment: item.widthAdjustment ?? "none",
                          heightAdjustment: item.heightAdjustment ?? "none",
                          chainType: item.chainType ?? "",
                        })
                      }
                    >
                      {t("store.checkout.remove", { defaultValue: "Remove" })}
                    </button>
                  </div>
                </div>
              );
              })
            )}
          </div>

          {cart.length > 0 ? (
            <>
              <h4 className="store-checkout-section-title">
                {t("store.checkout.deliveryDetails", {
                  defaultValue: "Delivery details",
                })}
              </h4>

              {savedAddresses.length === 0 ? (
                allowGuestCheckout ? (
                  <div className="store-form store-form--checkout-main">
                    <div className="store-checkout-guest-hint">
                      {t("store.checkout.guestHint", {
                        defaultValue:
                          "You can place this order as a guest. Sign in to save addresses for next time.",
                      })}
                    </div>

                    <div className="store-form-group">
                      <label htmlFor="checkout-fullName" className="store-form-label">
                        {t("store.checkout.fullName", { defaultValue: "Full name" })}
                      </label>
                      <input
                        id="checkout-fullName"
                        type="text"
                        value={checkoutForm.fullName}
                        onChange={(e) => onFormChange("fullName", e.target.value)}
                        autoComplete="name"
                        required
                      />
                    </div>

                    <div className="store-form-group">
                      <label htmlFor="checkout-phone" className="store-form-label">
                        {t("store.checkout.phone", { defaultValue: "Phone" })}
                      </label>
                      <input
                        id="checkout-phone"
                        type="tel"
                        value={checkoutForm.phone}
                        onChange={(e) => onFormChange("phone", e.target.value)}
                        autoComplete="tel"
                        dir="ltr"
                        required
                      />
                    </div>

                    <div className="store-form-group">
                      <label htmlFor="checkout-email" className="store-form-label">
                        {t("store.checkout.email", { defaultValue: "Email (optional)" })}
                      </label>
                      <input
                        id="checkout-email"
                        type="email"
                        value={checkoutForm.email}
                        onChange={(e) => onFormChange("email", e.target.value)}
                        autoComplete="email"
                        dir="ltr"
                      />
                    </div>

                    <div className="store-form-group store-checkout-guest-location">
                      <span className="store-form-label">
                        {t("store.checkout.address", { defaultValue: "Delivery address" })}
                      </span>
                      <p className="store-checkout-guest-location-intro">
                        {t("store.checkout.guestLocationIntro", {
                          defaultValue:
                            "Use the same map and structured address as signed-in customers. Nothing is saved to an account and it clears when you refresh the page.",
                        })}
                      </p>
                      <button
                        type="button"
                        className="store-checkout-guest-map-btn"
                        onClick={() => onOpenGuestAddressModal?.()}
                      >
                        {t("store.checkout.guestSetAddressMap", {
                          defaultValue: "Set address & map location",
                        })}
                      </button>
                      <label htmlFor="checkout-address" className="store-form-label store-checkout-guest-address-label">
                        {t("store.checkout.addressOrEdit", {
                          defaultValue: "Address text (filled from the button above, or type manually)",
                        })}
                      </label>
                      <textarea
                        id="checkout-address"
                        value={checkoutForm.address}
                        onChange={(e) => onFormChange("address", e.target.value)}
                        placeholder={t("store.checkout.addressPlaceholder", {
                          defaultValue: "Street, building, apartment, city…",
                        })}
                        required
                      />
                    </div>

                    <div className="store-form-group">
                      <label htmlFor="checkout-note" className="store-form-label">
                        {t("store.checkout.orderNote", {
                          defaultValue: "Order note (optional)",
                        })}
                      </label>
                      <textarea
                        id="checkout-note"
                        placeholder={t("store.checkout.orderNotePlaceholder", {
                          defaultValue: "Order note (optional)",
                        })}
                        value={checkoutForm.note}
                        onChange={(e) => onFormChange("note", e.target.value)}
                      />
                    </div>

                    <div className="store-form-group">
                      <label htmlFor="checkout-payment" className="store-form-label">
                        {t("store.checkout.paymentMethod", {
                          defaultValue: "Payment method",
                        })}
                      </label>
                      <select
                        id="checkout-payment"
                        value={checkoutForm.paymentMethod}
                        onChange={(e) => onFormChange("paymentMethod", e.target.value)}
                        aria-label={t("store.checkout.paymentAria", {
                          defaultValue: "Payment method",
                        })}
                      >
                        <option value="online">
                          {t("store.checkout.paymentOnline", {
                            defaultValue: "Online payment",
                          })}
                        </option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="store-checkout-saved-empty">
                    <p className="store-checkout-saved-empty-text">
                      {t("store.checkout.savedEmptyText", {
                        defaultValue:
                          "Add where we should deliver this order. You can save up to 5 addresses.",
                      })}
                    </p>
                    <button
                      type="button"
                      className="store-checkout-add-address-primary"
                      onClick={onOpenAddAddressModal}
                    >
                      {t("store.checkout.addAddressPrimary", {
                        defaultValue: "Add a new delivery address",
                      })}
                    </button>
                  </div>
                )
              ) : (
                <div className="store-checkout-saved-block">
                  <div className="store-form-label store-checkout-saved-heading">
                    {t("store.checkout.savedAddressesHeading", {
                      defaultValue: "Saved addresses",
                    })}
                  </div>
                  <ul
                    className="store-checkout-saved-list"
                    role="radiogroup"
                    aria-label={t("store.checkout.chooseAddressAria", {
                      defaultValue: "Choose delivery address",
                    })}
                  >
                    {savedAddresses.map((addr) => {
                      const idStr = String(addr._id);
                      const selected =
                        String(selectedSavedAddressId) === idStr;
                      return (
                        <li
                          key={idStr}
                          className={`store-checkout-saved-item${selected ? " is-selected" : ""}`}
                        >
                          <label className="store-checkout-saved-label">
                            <input
                              type="radio"
                              name="checkout-saved-address"
                              checked={selected}
                              onChange={() => onSelectSavedAddress(addr._id)}
                            />
                            <div className="store-checkout-saved-body">
                              <div className="store-checkout-saved-title-row">
                                {addr.label ? (
                                  <span className="store-checkout-saved-tag">
                                    {addr.label}
                                  </span>
                                ) : null}
                                <span className="store-checkout-saved-line">
                                  {formatSavedAddressSummary(addr)}
                                </span>
                              </div>
                              <span className="store-checkout-saved-meta">
                                {addr.fullName} · {addr.phone}
                              </span>
                            </div>
                          </label>
                          <button
                            type="button"
                            className="store-checkout-saved-delete"
                            onClick={() => onDeleteSavedAddress(addr._id)}
                          >
                            {t("store.checkout.deleteAddress", {
                              defaultValue: "Delete",
                            })}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {canAddMoreAddresses ? (
                    <button
                      type="button"
                      className="store-checkout-add-address-secondary"
                      onClick={onOpenAddAddressModal}
                    >
                      {t("store.checkout.addAddressSecondary", {
                        defaultValue: "+ Add a new delivery address",
                      })}
                    </button>
                  ) : (
                    <p className="store-checkout-saved-cap">
                      {t("store.checkout.savedCap", {
                        defaultValue:
                          "You can save up to 5 addresses. Remove one to add another.",
                      })}
                    </p>
                  )}
                </div>
              )}

              {savedAddresses.length > 0 && hasSavedSelection ? (
                <div className="store-form store-form--checkout-main">
                  <div className="store-checkout-selected-summary">
                    <span className="store-form-label">
                      {t("store.checkout.deliveringTo", {
                        defaultValue: "Delivering to",
                      })}
                    </span>
                    <p className="store-checkout-selected-name">
                      <strong>{checkoutForm.fullName}</strong>
                      <span className="store-checkout-selected-sep"> · </span>
                      {checkoutForm.phone}
                      {checkoutForm.email ? (
                        <>
                          <span className="store-checkout-selected-sep"> · </span>
                          {checkoutForm.email}
                        </>
                      ) : null}
                    </p>
                    <div className="store-checkout-selected-address">
                      {checkoutForm.address}
                    </div>
                  </div>

                  <div className="store-form-group">
                    <label htmlFor="checkout-note" className="store-form-label">
                      {t("store.checkout.orderNote", {
                        defaultValue: "Order note (optional)",
                      })}
                    </label>
                    <textarea
                      id="checkout-note"
                      placeholder={t("store.checkout.orderNotePlaceholder", {
                        defaultValue: "Order note (optional)",
                      })}
                      value={checkoutForm.note}
                      onChange={(e) => onFormChange("note", e.target.value)}
                    />
                  </div>
                  <div className="store-form-group">
                    <label htmlFor="checkout-payment" className="store-form-label">
                      {t("store.checkout.paymentMethod", {
                        defaultValue: "Payment method",
                      })}
                    </label>
                    <select
                      id="checkout-payment"
                      value={checkoutForm.paymentMethod}
                      onChange={(e) =>
                        onFormChange("paymentMethod", e.target.value)
                      }
                      aria-label={t("store.checkout.paymentAria", {
                        defaultValue: "Payment method",
                      })}
                    >
                      <option value="online">
                        {t("store.checkout.paymentOnline", {
                          defaultValue: "Online payment",
                        })}
                      </option>
                    </select>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <aside className="store-checkout-sidebar-card">
          <h4 className="store-checkout-summary-title">
            {t("store.checkout.orderSummary", {
              defaultValue: "Order summary",
            })}
          </h4>
          <div className="store-total">
            <div className="store-total-row">
              <span>
                {t("store.checkout.subtotal", { defaultValue: "Subtotal" })}
              </span>
              <strong>
                <StorePriceDisplay amount={cartTotal || 0} />
              </strong>
            </div>
            <div className="store-total-row">
              <span>
                {t("store.checkout.shipping", { defaultValue: "Shipping" })}
              </span>
              <strong>
                <StorePriceDisplay amount={shippingFee} />
              </strong>
            </div>
            <div className="store-total-row store-total-row--grand">
              <span>
                {t("store.checkout.total", { defaultValue: "Total" })}
              </span>
              <strong>
                <StorePriceDisplay amount={grandTotal} />
              </strong>
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting || cart.length === 0 || !deliveryReady}
            className="store-form-submit store-form-submit--sidebar"
          >
            {submitting
              ? t("store.checkout.placingOrder", {
                  defaultValue: "Placing order…",
                })
              : t("store.checkout.placeOrder", {
                  defaultValue: "Place order",
                })}
          </button>
          {cart.length > 0 && !deliveryReady ? (
            <p className="store-checkout-sidebar-hint">
              {allowGuestCheckout
                ? t("store.checkout.hintNeedAddressGuest", {
                    defaultValue:
                      "Enter your name, phone, and a delivery address (use “Set address & map” or type in the box).",
                  })
                : savedAddresses.length === 0
                  ? t("store.checkout.hintNeedFirstSavedAddress", {
                      defaultValue:
                        "Add your first delivery address with the button above, then save it to continue.",
                    })
                  : t("store.checkout.hintNeedAddress", {
                      defaultValue:
                        "Add a delivery address and keep one selected to place your order.",
                    })}
            </p>
          ) : null}
          {cart.length === 0 ? (
            <p className="store-checkout-sidebar-hint">
              {t("store.checkout.hintEmptyCart", {
                defaultValue: "Add items to your cart to check out.",
              })}
            </p>
          ) : null}
        </aside>
      </form>
    </div>
  );
});

export default CartCheckout;
